import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '@/firebase';

export interface DayCount {
  date: string;
  total: number;
  completed: number;
}

export interface MemberScore {
  memberId: string;
  name: string;
  score: number;
}

export interface CategoryCount {
  category: string;
  count: number;
}

export function useHouseholdAnalytics(
  householdId: string | null,
  memberIds: string[]
): {
  taskCompletionByDay: DayCount[];
  contributionByMember: MemberScore[];
  spendByCategory: CategoryCount[];
  loading: boolean;
} {
  const [taskCompletionByDay, setTaskCompletionByDay] = useState<DayCount[]>([]);
  const [contributionByMember, setContributionByMember] = useState<MemberScore[]>([]);
  const [spendByCategory, setSpendByCategory] = useState<CategoryCount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!householdId) {
        setLoading(false);
        return;
      }

      try {
        // Calculate date range
        const thirtyDaysAgo = Timestamp.fromDate(
          new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        );

        // 1. Task Completion by Day
        const tasksQuery = query(
          collection(db, 'households', householdId, 'tasks'),
          where('createdAt', '>=', thirtyDaysAgo)
        );
        const tasksSnapshot = await getDocs(tasksQuery);

        const dayMap = new Map<string, { total: number; completed: number }>();
        const last14Days: string[] = [];

        // Generate last 14 days
        for (let i = 13; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dateStr = date.toISOString().slice(0, 10);
          last14Days.push(dateStr);
          dayMap.set(dateStr, { total: 0, completed: 0 });
        }

        tasksSnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const taskDate = data.createdAt?.toDate?.().toISOString().slice(0, 10);
          if (taskDate && dayMap.has(taskDate)) {
            const current = dayMap.get(taskDate)!;
            current.total += 1;
            if (data.status === 'done' || data.status === 'DONE') {
              current.completed += 1;
            }
          }
        });

        const taskData: DayCount[] = last14Days.map(date => ({
          date,
          total: dayMap.get(date)?.total || 0,
          completed: dayMap.get(date)?.completed || 0,
        }));
        setTaskCompletionByDay(taskData);

        // 2. Contribution by Member
        const memberPromises = memberIds.map(async (memberId) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', memberId));
            const userData = userDoc.data();
            return {
              memberId,
              name: userData?.displayName || userData?.name || memberId.slice(0, 8),
              score: userData?.contributionScore || 0,
            };
          } catch {
            return {
              memberId,
              name: memberId.slice(0, 8),
              score: 0,
            };
          }
        });

        const memberScores = await Promise.all(memberPromises);
        setContributionByMember(memberScores);

        // 3. Items by Category
        const inventoryQuery = collection(db, 'households', householdId, 'inventory');
        const inventorySnapshot = await getDocs(inventoryQuery);

        const categoryMap = new Map<string, number>();
        inventorySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const category = data.category || 'Uncategorized';
          categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
        });

        const categoryData: CategoryCount[] = Array.from(categoryMap.entries())
          .map(([category, count]) => ({ category, count }))
          .sort((a, b) => b.count - a.count);

        setSpendByCategory(categoryData);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [householdId, memberIds]);

  return {
    taskCompletionByDay,
    contributionByMember,
    spendByCategory,
    loading,
  };
}